<script setup lang="ts">
import type { Component } from 'vue';

const props = defineProps<{
  icon: Component;
  color: string;
}>();
</script>

<template>
  <div class="nx-timeline">
    <div class="nx-timeline-icon" :style="`color: ${props.color}`">
      <component :is="props.icon" />
    </div>
  </div>
  <slot />
</template>

<style scoped>
/* No --nxt-timeline-* component-token family exists yet (I-596 / #555 gap) —
   the connecting line and icon ring read the closest role tokens instead:
   --nxt-border for the line. The marker colour stays caller-driven via the
   `color` prop (e.g. a --nxt-{role}-bg value) and is drawn as a tinted disc
   through currentColor, like the NxProcessSteps discs. */
.nx-timeline {
  padding-top: var(--icon-y-off);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: calc(var(--icon-size) / 2);
    top: calc(-1 * var(--gap) / 2);
    height: calc(var(--icon-y-off) + var(--gap) / 2);
    transform: translateX(-50%);
    width: calc(var(--nxt-hairline) * 2);
    border-radius: var(--nxt-radius-full);
    background-color: var(--nxt-border);
  }
  /* Split line into before and after to allow for transparent bg */
  &::after {
    content: '';
    position: absolute;
    left: calc(var(--icon-size) / 2);
    top: calc(var(--icon-y-off) + var(--icon-size));
    bottom: calc(-1 * var(--gap) / 2);
    transform: translateX(-50%);
    width: calc(var(--nxt-hairline) * 2);
    border-radius: var(--nxt-radius-full);
    background-color: var(--nxt-border);
  }
}

[data-top-end='icon'] .nx-timeline:first-child::before {
  content: none;
}
[data-bottom-end='icon'] .nx-timeline:nth-last-child(2)::after {
  content: none;
}

.nx-timeline-icon {
  position: relative;
  width: var(--icon-size);
  height: var(--icon-size);
  border-radius: var(--nxt-radius-full);
  border: var(--nxt-hairline) solid color-mix(in srgb, currentColor 40%, transparent);
  background-color: color-mix(in srgb, currentColor 14%, var(--nxt-surface-bg));

  & > * {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 60%;
    height: 60%;
    transform: translate(-50%, -50%);
  }
}
</style>
